// Carteiras (/wallets) — contatos com atendente e fila fixos.
// Mockup: "Aeros Dark — Carteiras (/wallets)".
//
// Contato em carteira volta sempre para o mesmo atendente, ignorando o
// rodízio da fila — é o que a nota do rodapé explica, porque a consequência
// não é óbvia para quem só vê a lista.
import React, { useCallback, useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import PersonRemoveIcon from "@material-ui/icons/PersonAddDisabled";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import AerosDialog from "../../components/AerosDialog";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS } from "../../styles/aerosTokens";

const GRID = "minmax(0,1.5fr) minmax(0,1.1fr) 150px 170px minmax(0,1.2fr) 96px";

const reducer = (state, action) => {
  if (action.type === "LOAD_WALLETS") {
    const novas = [];
    action.payload.forEach((w) => {
      const i = state.findIndex((x) => x.id === w.id);
      if (i !== -1) state[i] = w;
      else novas.push(w);
    });
    return [...state, ...novas];
  }
  if (action.type === "UPDATE_WALLET") {
    const w = action.payload;
    const i = state.findIndex((x) => x.contactId === w.contactId);
    if (i !== -1) {
      state[i] = { ...state[i], ...w };
      return [...state];
    }
    return state;
  }
  if (action.type === "DELETE_WALLET") {
    return state.filter((w) => w.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const iniciais = (nome = "") =>
  nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    headerSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    rowHead: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 10,
      padding: "10px 16px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      borderBottom: `1px solid ${border}`,
    },
    row: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 10,
      padding: "11px 16px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"
      }`,
      alignItems: "center",
      fontSize: 12.5,
    },
    pessoa: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    avatar: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      fontSize: 11,
      fontWeight: 700,
      color: isLight ? "#FFFFFF" : "#07080D",
    },
    nome: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    filaPill: {
      display: "inline-flex",
      alignItems: "center",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 10px",
      whiteSpace: "nowrap",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    mono: {
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    email: {
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    acoes: { display: "flex", gap: 2, justifyContent: "flex-end" },
    vazio: {
      padding: "28px 16px",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },
    nota: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginTop: 12,
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 15, flex: "none" },
    },
    filtro: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: "transparent",
        fontSize: 12.5,
        fontWeight: 600,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isLight
          ? theme.palette.aeros.borderStrong
          : "rgba(255,255,255,0.14)",
      },
      "& .MuiSelect-root": {
        padding: "9px 30px 9px 12px",
        color: theme.palette.text.secondary,
      },
      "& .MuiSelect-icon": { color: theme.palette.text.secondary },
    },
  };
});

const Wallets = () => {
  const c = useCampaignPageStyles();
  const m = useAerosModalStyles();
  const classes = useStyles();
  const theme = useTheme();

  const [wallets, dispatch] = useReducer(reducer, []);
  const [termo, setTermo] = useState("");
  const [usuarioFiltro, setUsuarioFiltro] = useState("");
  const [pagina, setPagina] = useState(1);
  const [temMais, setTemMais] = useState(false);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [usuarios, setUsuarios] = useState([]);

  const [removendo, setRemovendo] = useState(null);
  const [editando, setEditando] = useState(null);
  const [usuarioEscolhido, setUsuarioEscolhido] = useState("");
  const [filaEscolhida, setFilaEscolhida] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPagina(1);
  }, [termo, usuarioFiltro]);

  useEffect(() => {
    setCarregando(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/wallets", {
          params: {
            searchParam: termo,
            pageNumber: pagina,
            ...(usuarioFiltro ? { userId: usuarioFiltro } : {}),
          },
        });
        dispatch({ type: "LOAD_WALLETS", payload: data.wallets || [] });
        setTemMais(Boolean(data.hasMore));
        setTotal(Number(data.count) || 0);
      } catch (err) {
        toastError(err);
      }
      setCarregando(false);
    }, 400);
    return () => clearTimeout(t);
  }, [termo, usuarioFiltro, pagina]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/");
        setUsuarios(data?.users || []);
      } catch (err) {
        setUsuarios([]);
      }
    })();
  }, []);

  // Filas oferecidas para o atendente escolhido. A fila já gravada entra na
  // lista mesmo que o atendente não esteja mais associado a ela: sem isso,
  // uma carteira antiga não poderia ser salva de novo e o valor atual sumiria
  // da tela.
  const filasDoUsuario = useCallback(
    (userId) => {
      const filas = usuarios.find((u) => u.id === Number(userId))?.queues || [];
      const atual = editando;
      if (
        atual?.queueId &&
        Number(atual.userId) === Number(userId) &&
        !filas.some((q) => Number(q.id) === Number(atual.queueId))
      ) {
        return [...filas, { id: atual.queueId, name: atual.queueName }];
      }
      return filas;
    },
    [usuarios, editando]
  );

  const handleRemover = async () => {
    if (!removendo) return;
    try {
      await api.delete(`/contacts/wallet/${removendo.contactId}`);
      dispatch({ type: "DELETE_WALLET", payload: removendo.id });
      toast.success(i18n.t("aeros.wallets.deleteSuccess"));
    } catch (err) {
      toastError(err);
    }
    setRemovendo(null);
  };

  const handleAbrirEdicao = (wallet) => {
    setEditando(wallet);
    setUsuarioEscolhido(wallet.userId || "");
    setFilaEscolhida(wallet.queueId || "");
  };

  const handleSalvar = async () => {
    if (!usuarioEscolhido || !filaEscolhida) {
      toast.error(i18n.t("aeros.wallets.selectUserAndQueue"));
      return;
    }
    setSalvando(true);
    try {
      await api.put(`/contacts/wallet/${editando.contactId}`, {
        wallets: { userId: usuarioEscolhido, queueId: filaEscolhida },
      });
      const usuario = usuarios.find((u) => u.id === Number(usuarioEscolhido));
      const fila = filasDoUsuario(usuarioEscolhido).find(
        (q) => q.id === Number(filaEscolhida)
      );
      dispatch({
        type: "UPDATE_WALLET",
        payload: {
          contactId: editando.contactId,
          userId: usuarioEscolhido,
          userName: usuario?.name || editando.userName,
          queueId: filaEscolhida,
          queueName: fila?.name || editando.queueName,
        },
      });
      toast.success(i18n.t("aeros.wallets.updateSuccess"));
      setEditando(null);
    } catch (err) {
      toastError(err);
    }
    setSalvando(false);
  };

  const handleScroll = (e) => {
    if (!temMais || carregando) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPagina((p) => p + 1);
    }
  };

  const corDe = (semente) =>
    AEROS.avatarCycle[Math.abs(Number(semente) || 0) % AEROS.avatarCycle.length];

  return (
    <MainContainer>
      <AerosConfirmModal
        open={Boolean(removendo)}
        onClose={() => setRemovendo(null)}
        onConfirm={handleRemover}
        icon={<PersonRemoveIcon />}
        tone="danger"
        title={i18n.t("aeros.wallets.confirmTitle", {
          name: removendo?.contactName || "",
        })}
        description={i18n.t("aeros.wallets.confirmMessage")}
        confirmLabel={i18n.t("aeros.wallets.remove")}
      />

      <AerosDialog
        open={Boolean(editando)}
        onClose={() => setEditando(null)}
        width={480}
        title={i18n.t("aeros.wallets.editTitle")}
        subtitle={editando?.contactName}
        onSubmit={handleSalvar}
        submitLabel={i18n.t("aeros.wallets.save")}
        loading={salvando}
      >
        <div>
          <label className={m.fieldLabel}>{i18n.t("aeros.wallets.user")}</label>
          <TextField
            select
            fullWidth
            variant="outlined"
            className={m.input}
            value={usuarioEscolhido}
            onChange={(e) => {
              setUsuarioEscolhido(e.target.value);
              // Fila zerada de propósito: as filas mudam com o atendente, e
              // manter a anterior gravaria um par usuário/fila inválido.
              const filas = filasDoUsuario(e.target.value);
              setFilaEscolhida(filas.length === 1 ? filas[0].id : "");
            }}
          >
            {usuarios.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div>
          <label className={m.fieldLabel}>{i18n.t("aeros.wallets.queue")}</label>
          <TextField
            select
            fullWidth
            variant="outlined"
            className={m.input}
            value={filaEscolhida}
            onChange={(e) => setFilaEscolhida(e.target.value)}
            disabled={!usuarioEscolhido}
            helperText={
              usuarioEscolhido && filasDoUsuario(usuarioEscolhido).length === 0
                ? i18n.t("aeros.wallets.noQueues")
                : ""
            }
          >
            {filasDoUsuario(usuarioEscolhido).map((q) => (
              <MenuItem key={q.id} value={q.id}>
                {q.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </AerosDialog>

      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>
            {i18n.t("aeros.wallets.title")}{" "}
            <span className={c.pageTitleCount}>· {total}</span>
          </div>
          <div className={classes.headerSubtitle}>
            {i18n.t("aeros.wallets.subtitle")}
          </div>
        </div>
        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("aeros.wallets.search")}
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.wallets.search") }}
          />
          <TextField
            select
            size="small"
            variant="outlined"
            className={classes.filtro}
            style={{ width: 210 }}
            value={usuarioFiltro}
            onChange={(e) => setUsuarioFiltro(e.target.value)}
            SelectProps={{
              displayEmpty: true,
              "aria-label": i18n.t("aeros.wallets.filterUser"),
            }}
          >
            <MenuItem value="">{i18n.t("aeros.wallets.allUsers")}</MenuItem>
            {usuarios.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      <div className={c.pageBody} onScroll={handleScroll}>
        <div className={classes.card}>
          <div className={classes.rowHead}>
            <div>{i18n.t("aeros.wallets.contact")}</div>
            <div>{i18n.t("aeros.wallets.user")}</div>
            <div>{i18n.t("aeros.wallets.queue")}</div>
            <div>{i18n.t("aeros.wallets.phone")}</div>
            <div>{i18n.t("aeros.wallets.email")}</div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.wallets.actions")}
            </div>
          </div>

          {carregando && wallets.length === 0 ? (
            <div className={classes.vazio}>{i18n.t("aeros.wallets.loading")}</div>
          ) : wallets.length === 0 ? (
            <div className={classes.vazio}>
              {termo || usuarioFiltro
                ? i18n.t("aeros.wallets.emptySearch")
                : i18n.t("aeros.wallets.empty")}
            </div>
          ) : (
            wallets.map((w) => (
              <div className={classes.row} key={w.id}>
                <div className={classes.pessoa}>
                  <div
                    className={classes.avatar}
                    style={{ backgroundColor: corDe(w.contactId) }}
                  >
                    {iniciais(w.contactName)}
                  </div>
                  <span className={classes.nome} title={w.contactName}>
                    {w.contactName}
                  </span>
                </div>

                <div className={classes.pessoa}>
                  <div
                    className={classes.avatar}
                    style={{
                      backgroundColor: corDe((Number(w.userId) || 0) + 3),
                    }}
                  >
                    {iniciais(w.userName)}
                  </div>
                  <span className={classes.nome} title={w.userName}>
                    {w.userName}
                  </span>
                </div>

                <div>
                  {w.queueName ? (
                    <span
                      className={classes.filaPill}
                      style={{
                        color: theme.palette.text.primary,
                        backgroundColor: alpha(
                          corDe((Number(w.queueId) || 0) + 1),
                          0.16
                        ),
                      }}
                      title={w.queueName}
                    >
                      {w.queueName}
                    </span>
                  ) : (
                    <span className={classes.mono}>—</span>
                  )}
                </div>

                <div className={classes.mono} title={w.contactNumber}>
                  {w.contactNumber || "—"}
                </div>
                <div className={classes.email} title={w.contactEmail}>
                  {w.contactEmail || "—"}
                </div>

                <div className={classes.acoes}>
                  <Tooltip title={i18n.t("aeros.wallets.edit")}>
                    <IconButton
                      className={c.rowActionButton}
                      aria-label={i18n.t("aeros.wallets.edit")}
                      onClick={() => handleAbrirEdicao(w)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("aeros.wallets.remove")}>
                    <IconButton
                      className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                      aria-label={i18n.t("aeros.wallets.remove")}
                      onClick={() => setRemovendo(w)}
                    >
                      <PersonRemoveIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </div>

        <div className={classes.nota}>
          <InfoOutlinedIcon />
          {i18n.t("aeros.wallets.note")}
        </div>
      </div>
    </MainContainer>
  );
};

export default Wallets;
